"use client";

import { useState } from "react";
import { inputClass, labelClass, primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { Segmented } from "@/components/Segmented";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { usePdfSummaries } from "@/hooks/usePdfSummaries";
import { useToolFiles } from "@/hooks/useToolFiles";
import { outputName } from "@/lib/files";
import {
  eachPage,
  everyNPages,
  groupLabel,
  parseRanges,
  type Groups,
} from "@/lib/pdf/ranges";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
import { splitFile } from "@/workers/pdf-client";

const tool = getTool("split-pdf")!;

type Mode = "ranges" | "every" | "each";

/** "pages 1–3, 5" / "page 5" for people (file names use groupLabel). */
function describe(group: number[]) {
  const label = groupLabel(group); // "pages-1-3_5" or "page-5"
  const [word, numbers] = [
    label.slice(0, label.indexOf("-")),
    label.slice(label.indexOf("-") + 1),
  ];
  return `${word} ${numbers.replaceAll("_", ", ").replaceAll("-", "–")}`;
}

/** FR-11: page ranges, every N pages, or one file per page (ZIP). */
export function SplitTool() {
  const { files, setFiles, notices } = useToolFiles(tool);
  const file = files[0];
  const summary = usePdfSummaries(files).get(file);
  const pageCount = summary && "pages" in summary ? summary.pages : undefined;
  const [mode, setMode] = useState<Mode>("ranges");
  const [ranges, setRanges] = useState("1-3, 5");
  const [together, setTogether] = useState(false);
  const [n, setN] = useState("2");
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  let plan: { groups: Groups } | { error: string } | null = null;
  if (pageCount !== undefined) {
    if (mode === "each") plan = { groups: eachPage(pageCount) };
    else if (mode === "every") {
      const every = Number(n);
      plan =
        Number.isInteger(every) && every >= 1
          ? { groups: everyNPages(pageCount, every) }
          : { error: "Enter a whole number of pages, like 2." };
    } else {
      const parsed = parseRanges(ranges, pageCount);
      plan =
        "groups" in parsed && together
          ? { groups: [parsed.groups.flat()] }
          : parsed;
    }
  }
  const groups = plan && "groups" in plan ? plan.groups : [];
  const planError = plan && "error" in plan ? plan.error : "";

  async function split() {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const blobs = await splitFile(file, groups);
      setResults(
        blobs.map((blob, i) => ({
          blob,
          name: outputName(file.name, groupLabel(groups[i]), "pdf"),
          detail: `${groups[i].length} ${groups[i].length === 1 ? "page" : "pages"}`,
        })),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const base = file ? file.name.replace(/\.pdf$/i, "") : "pdf";

  return (
    <ToolShell
      tool={tool}
      files={files}
      onFilesChange={(next) => {
        setFiles(next);
        setResults(null);
        setError("");
      }}
      notices={notices}
      results={results}
      zipName={`${base}-split.zip`}
      onStartOver={() => {
        setFiles([]);
        setResults(null);
      }}
      fileDetails={[
        pageCount !== undefined
          ? `${pageCount} ${pageCount === 1 ? "page" : "pages"}`
          : undefined,
      ]}
    >
      {summary && "error" in summary ? (
        <p role="alert" className="font-medium text-destructive">
          {summary.error}
        </p>
      ) : (
        <section
          aria-label="Settings"
          className="flex flex-col gap-5 rounded-[16px] border bg-card p-4 sm:p-5"
        >
          <Segmented
            label="Split by"
            stretch
            value={mode}
            onChange={setMode}
            options={[
              { value: "ranges", label: "Page ranges" },
              { value: "every", label: "Every N pages" },
              { value: "each", label: "Each page" },
            ]}
          />

          {mode === "ranges" && (
            <div className="flex flex-col gap-3">
              <label className={labelClass}>
                Pages to keep
                <input
                  className={inputClass}
                  value={ranges}
                  onChange={(e) => setRanges(e.target.value)}
                  aria-describedby="ranges-hint"
                />
              </label>
              <p id="ranges-hint" className="text-sm text-muted-foreground">
                Separate with commas, e.g. 1-3, 5, 8-10. Each part becomes its
                own PDF. “8-” means page 8 to the end.
              </p>
              <label className="flex min-h-11 items-center gap-3 text-[15px] font-semibold">
                <input
                  type="checkbox"
                  className="size-[22px] accent-brand"
                  checked={together}
                  onChange={(e) => setTogether(e.target.checked)}
                />
                Put them all in one PDF
              </label>
            </div>
          )}

          {mode === "every" && (
            <label className={cn(labelClass, "max-w-xs")}>
              Pages per file
              <input
                className={inputClass}
                inputMode="numeric"
                value={n}
                onChange={(e) => setN(e.target.value)}
              />
            </label>
          )}

          <div className="rounded-lg bg-canvas p-3.5" aria-live="polite">
            {pageCount === undefined ? (
              <p className="text-muted-foreground">Reading your PDF…</p>
            ) : planError ? (
              <p role="alert" className="font-medium text-destructive">
                {planError}
              </p>
            ) : (
              <>
                <p className="font-semibold" data-testid="split-summary">
                  {groups.length} {groups.length === 1 ? "file" : "files"} from{" "}
                  {pageCount} pages
                </p>
                <p className="text-sm text-ink">
                  {groups.slice(0, 8).map(describe).join(" · ")}
                  {groups.length > 8 ? ` · and ${groups.length - 8} more` : ""}
                </p>
              </>
            )}
          </div>

          {error && (
            <p role="alert" className="font-medium text-destructive">
              {error}
            </p>
          )}

          {busy ? (
            <ProgressBar label="Splitting your PDF…" />
          ) : (
            <button
              type="button"
              onClick={split}
              disabled={groups.length === 0}
              className={primaryButtonClass}
            >
              Split PDF
            </button>
          )}
          <p className="-mt-2 text-center text-[13px] text-muted-foreground">
            {groups.length > 1 ? "Downloads as a ZIP · " : ""}nothing is
            uploaded
          </p>
        </section>
      )}
    </ToolShell>
  );
}
